import {
  Phone,
  PhoneOutgoing,
  MessageSquare,
  MessageSquarePlus,
  StickyNote,
  UserPlus,
  ArrowRightLeft,
  Mic,
  FileText,
  Activity,
} from "lucide-react";
import { RelativeTime } from "../shared/RelativeTime";
import type { TimelineEvent } from "./useContactTimeline";

const EVENT_CONFIG: Record<
  string,
  { icon: React.ComponentType<{ size?: number }>; label: string; color: string }
> = {
  call_inbound: { icon: Phone, label: "Inbound call", color: "#2563eb" },
  call_outbound: { icon: PhoneOutgoing, label: "Outbound call", color: "#7c3aed" },
  sms_inbound: { icon: MessageSquare, label: "SMS received", color: "#059669" },
  sms_outbound: { icon: MessageSquarePlus, label: "SMS sent", color: "#0891b2" },
  note_created: { icon: StickyNote, label: "Note added", color: "#d97706" },
  contact_created: { icon: UserPlus, label: "Contact created", color: "#16a34a" },
  status_change: { icon: ArrowRightLeft, label: "Status changed", color: "#dc2626" },
  recording_ready: { icon: Mic, label: "Recording ready", color: "#7c3aed" },
  transcript_ready: { icon: FileText, label: "Transcript ready", color: "#2563eb" },
};

const DEFAULT_CONFIG = { icon: Activity, label: "Event", color: "#6b7280" };

interface TimelineEntryProps {
  event: TimelineEvent;
}

export function TimelineEntry({ event }: TimelineEntryProps) {
  const config = EVENT_CONFIG[event.event_type] || DEFAULT_CONFIG;
  const Icon = config.icon;
  const payload = event.payload || {};

  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        padding: "12px 0",
        borderBottom: "1px solid #f3f4f6",
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          backgroundColor: `${config.color}15`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={16} style={{ color: config.color }} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 500, color: "#111827" }}>
            {config.label}
          </span>
          <RelativeTime date={event.created_at} className="" />
        </div>
        {renderPayloadDetails(event.event_type, payload)}
      </div>
    </div>
  );
}

function renderPayloadDetails(
  eventType: string,
  payload: Record<string, unknown>
) {
  const detailStyle = { fontSize: 12, color: "#6b7280", marginTop: 2 };

  switch (eventType) {
    case "sms_inbound":
    case "sms_outbound":
      return payload.body_preview ? (
        <div style={detailStyle}>"{String(payload.body_preview)}"</div>
      ) : null;
    case "call_inbound":
    case "call_outbound":
      return (
        <div style={detailStyle}>
          {payload.from_number && `From: ${String(payload.from_number)}`}
          {payload.duration_seconds && ` · ${String(payload.duration_seconds)}s`}
        </div>
      );
    case "recording_ready":
      return (
        <div style={detailStyle}>
          {payload.duration_seconds && `Duration: ${String(payload.duration_seconds)}s`}
        </div>
      );
    case "transcript_ready":
      return payload.transcript_preview ? (
        <div style={detailStyle}>
          "{String(payload.transcript_preview).substring(0, 120)}…"
        </div>
      ) : null;
    case "contact_created":
      return payload.source ? (
        <div style={detailStyle}>Source: {String(payload.source)}</div>
      ) : null;
    case "status_change":
      return (
        <div style={detailStyle}>
          {payload.from && `${String(payload.from)} → `}
          {payload.to && String(payload.to)}
        </div>
      );
    default:
      return null;
  }
}
